import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Camera,
  Check,
  X,
  RotateCcw,
  AlertCircle,
  Loader2,
  Upload,
  ImageIcon,
  Focus,
  Sun,
  Smartphone,
  Zap,
  ZapOff,
  AlertTriangle,
  Eye,
  CheckCircle2,
  XCircle,
  Info,
  FileText,
  ScanLine,
  Crop,
} from 'lucide-react';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// FundElite colors
const colors = {
  brandGreen: '#0FAC6C',
  brandGreen600: '#0D9F64',
  gold: '#D4AF37',
  white: '#FFFFFF',
  surface: '#F7F9F8',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#6B7280',
  borderDefault: '#E5E9E7',
  error: '#EF4444',
  warning: '#F59E0B',
  dark: '#1a1a1a',
  darkButton: '#111827',
  darkButtonHover: '#1f2937',
  darkButtonActive: '#243244',
};

// Quality thresholds
const QUALITY_THRESHOLDS = {
  brightness: { min: 50, max: 210 },
  contrast: { min: 30 },
  blur: { min: 100 }, // Laplacian variance - higher is sharper
  minDimension: 640,
  // Document detection
  minAreaRatio: 0.15, // Document must be at least 15% of image area
  maxAreaRatio: 0.95, // Document can't be more than 95% of image
};

// Quality status labels
const QUALITY_STATUS = {
  good: { label: 'Good Quality', color: colors.brandGreen, icon: CheckCircle2 },
  needs_review: { label: 'Needs Review', color: colors.warning, icon: AlertTriangle },
  poor: { label: 'Poor Quality', color: colors.error, icon: XCircle },
};

// Warning messages with priority
const WARNING_MESSAGES = {
  no_document: {
    priority: 100,
    message: 'Document not detected. Please capture a full page inside the frame.',
    short: 'No document detected',
  },
  document_cutoff: {
    priority: 90,
    message: 'Document edges not fully visible. Position the entire page inside the frame.',
    short: 'Edges not visible',
  },
  dark: {
    priority: 80,
    message: 'Image is too dark. Use flash or move to better lighting.',
    short: 'Too dark',
  },
  bright: {
    priority: 75,
    message: 'Image is too bright. Reduce lighting or move away from direct light.',
    short: 'Too bright',
  },
  blur: {
    priority: 70,
    message: 'Image is not clear enough to read. Hold steady and retake.',
    short: 'Not clear enough',
  },
  contrast: {
    priority: 60,
    message: 'Text may not be readable. Retake with better focus and lighting.',
    short: 'Low contrast',
  },
  resolution: {
    priority: 50,
    message: 'Image resolution is low. Move closer to the document.',
    short: 'Low resolution',
  },
};

// OpenCV.js loading state
let cvLoaded = false;
let cvLoadPromise = null;

/**
 * Load OpenCV.js dynamically with timeout
 */
const loadOpenCV = () => {
  if (cvLoaded && window.cv) {
    return Promise.resolve(window.cv);
  }
  
  if (cvLoadPromise) {
    return cvLoadPromise;
  }
  
  cvLoadPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.cv && window.cv.Mat) {
      cvLoaded = true;
      resolve(window.cv);
      return;
    }
    
    // Timeout after 15 seconds - scanner will work without OpenCV
    const timeout = setTimeout(() => {
      console.warn('[OpenCV] Load timeout - using fallback mode');
      reject(new Error('OpenCV load timeout'));
    }, 15000);
    
    const script = document.createElement('script');
    script.src = 'https://docs.opencv.org/4.9.0/opencv.js';
    script.async = true;
    
    script.onload = () => {
      // OpenCV.js sets cv as a function initially, need to wait for Module
      let checkCount = 0;
      const maxChecks = 50; // 5 seconds max wait for runtime init
      
      const checkCV = () => {
        if (window.cv && window.cv.Mat) {
          clearTimeout(timeout);
          cvLoaded = true;
          console.log('[OpenCV] Loaded successfully');
          resolve(window.cv);
        } else if (window.cv) {
          // cv exists but not ready, wait
          window.cv.onRuntimeInitialized = () => {
            clearTimeout(timeout);
            cvLoaded = true;
            console.log('[OpenCV] Runtime initialized');
            resolve(window.cv);
          };
        } else if (checkCount < maxChecks) {
          checkCount++;
          setTimeout(checkCV, 100);
        } else {
          clearTimeout(timeout);
          console.warn('[OpenCV] Runtime init timeout');
          reject(new Error('OpenCV runtime init timeout'));
        }
      };
      checkCV();
    };
    
    script.onerror = () => {
      clearTimeout(timeout);
      console.error('[OpenCV] Failed to load');
      reject(new Error('Failed to load OpenCV.js'));
    };
    
    document.head.appendChild(script);
  });
  
  return cvLoadPromise;
};

/**
 * Document Scanner using OpenCV.js
 * Detects document boundaries and analyzes quality
 */
class DocumentScanner {
  constructor(cv) {
    this.cv = cv;
  }
  
  /**
   * Analyze image and detect document
   * Returns quality metrics, warnings, and detected contour
   */
  analyze(canvas) {
    const cv = this.cv;
    const width = canvas.width;
    const height = canvas.height;
    
    // Read image from canvas
    const src = cv.imread(canvas);
    const gray = new cv.Mat();
    const blurred = new cv.Mat();
    const edges = new cv.Mat();
    
    const warnings = [];
    let documentContour = null;
    let documentDetected = false;
    let perspectiveResult = null;
    
    try {
      // Convert to grayscale
      cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
      
      // === QUALITY METRICS ===
      
      // 1. Brightness (mean luminance)
      const meanScalar = cv.mean(gray);
      const brightness = meanScalar[0];
      
      // 2. Contrast (standard deviation)
      const stdDev = new cv.Mat();
      const meanMat = new cv.Mat();
      cv.meanStdDev(gray, meanMat, stdDev);
      const contrast = stdDev.data64F[0];
      stdDev.delete();
      meanMat.delete();
      
      // 3. Blur detection (Laplacian variance)
      const laplacian = new cv.Mat();
      cv.Laplacian(gray, laplacian, cv.CV_64F);
      const lapMean = new cv.Mat();
      const lapStdDev = new cv.Mat();
      cv.meanStdDev(laplacian, lapMean, lapStdDev);
      const blurScore = lapStdDev.data64F[0] * lapStdDev.data64F[0]; // variance
      laplacian.delete();
      lapMean.delete();
      lapStdDev.delete();
      
      // === DOCUMENT DETECTION ===
      
      // Apply Gaussian blur to reduce noise
      cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);
      
      // Canny edge detection
      cv.Canny(blurred, edges, 50, 150);
      
      // Dilate to close gaps in edges
      const kernel = cv.Mat.ones(3, 3, cv.CV_8U);
      cv.dilate(edges, edges, kernel);
      kernel.delete();
      
      // Find contours
      const contours = new cv.MatVector();
      const hierarchy = new cv.Mat();
      cv.findContours(edges, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);
      
      const imageArea = width * height;
      let maxArea = 0;
      let bestContour = null;
      let bestApprox = null;
      
      // Find the largest 4-corner contour (document)
      for (let i = 0; i < contours.size(); i++) {
        const contour = contours.get(i);
        const area = cv.contourArea(contour);
        
        // Skip too small or too large contours
        const areaRatio = area / imageArea;
        if (areaRatio < QUALITY_THRESHOLDS.minAreaRatio || areaRatio > QUALITY_THRESHOLDS.maxAreaRatio) {
          continue;
        }
        
        // Approximate the contour
        const peri = cv.arcLength(contour, true);
        const approx = new cv.Mat();
        cv.approxPolyDP(contour, approx, 0.02 * peri, true);
        
        // Check if it's a quadrilateral (4 corners)
        if (approx.rows === 4 && area > maxArea) {
          // Check if it's convex
          if (cv.isContourConvex(approx)) {
            maxArea = area;
            if (bestApprox) bestApprox.delete();
            bestApprox = approx;
            bestContour = contour;
          } else {
            approx.delete();
          }
        } else {
          approx.delete();
        }
      }
      
      // Check if we found a valid document contour
      if (bestApprox && maxArea > 0) {
        documentDetected = true;
        
        // Extract corner points
        const corners = [];
        for (let i = 0; i < 4; i++) {
          corners.push({
            x: bestApprox.data32S[i * 2],
            y: bestApprox.data32S[i * 2 + 1],
          });
        }
        
        // Order corners: top-left, top-right, bottom-right, bottom-left
        documentContour = this.orderCorners(corners);
        
        // Check if document edges are too close to image edges (cutoff)
        const margin = Math.min(width, height) * 0.03;
        const isCutoff = documentContour.some(p => 
          p.x < margin || p.x > width - margin || 
          p.y < margin || p.y > height - margin
        );
        
        if (isCutoff) {
          warnings.push({ type: 'document_cutoff', ...WARNING_MESSAGES.document_cutoff });
        }
        
        // Prepare perspective transform result
        perspectiveResult = {
          corners: documentContour,
          canTransform: true,
        };
        
        bestApprox.delete();
      } else {
        // No document detected
        warnings.push({ type: 'no_document', ...WARNING_MESSAGES.no_document });
      }
      
      // Clean up contours
      contours.delete();
      hierarchy.delete();
      
      // === ADD QUALITY WARNINGS (only if document detected) ===
      if (documentDetected) {
        if (brightness < QUALITY_THRESHOLDS.brightness.min) {
          warnings.push({ type: 'dark', ...WARNING_MESSAGES.dark });
        } else if (brightness > QUALITY_THRESHOLDS.brightness.max) {
          warnings.push({ type: 'bright', ...WARNING_MESSAGES.bright });
        }
        
        if (contrast < QUALITY_THRESHOLDS.contrast.min) {
          warnings.push({ type: 'contrast', ...WARNING_MESSAGES.contrast });
        }
        
        if (blurScore < QUALITY_THRESHOLDS.blur.min) {
          warnings.push({ type: 'blur', ...WARNING_MESSAGES.blur });
        }
      }
      
      // Resolution check (always)
      const minDim = Math.min(width, height);
      if (minDim < QUALITY_THRESHOLDS.minDimension) {
        warnings.push({ type: 'resolution', ...WARNING_MESSAGES.resolution });
      }
      
      // Sort warnings by priority
      warnings.sort((a, b) => b.priority - a.priority);
      
      // Determine quality status
      let status = 'good';
      if (warnings.length > 0) {
        const hasCritical = warnings.some(w => ['no_document', 'blur', 'dark'].includes(w.type));
        status = hasCritical ? 'poor' : 'needs_review';
      }
      
      return {
        brightness,
        contrast,
        blurScore,
        documentDetected,
        documentContour,
        perspectiveResult,
        dimensions: { width, height },
        warnings,
        status,
      };
      
    } finally {
      // Clean up OpenCV matrices
      src.delete();
      gray.delete();
      blurred.delete();
      edges.delete();
    }
  }
  
  /**
   * Order corner points: top-left, top-right, bottom-right, bottom-left
   */
  orderCorners(corners) {
    // Sort by y to get top and bottom pairs
    const sorted = [...corners].sort((a, b) => a.y - b.y);
    const top = sorted.slice(0, 2).sort((a, b) => a.x - b.x);
    const bottom = sorted.slice(2, 4).sort((a, b) => a.x - b.x);
    
    return [
      top[0],     // top-left
      top[1],     // top-right
      bottom[1],  // bottom-right
      bottom[0],  // bottom-left
    ];
  }
  
  /**
   * Apply perspective transformation to crop and straighten document
   */
  applyPerspectiveTransform(canvas, corners) {
    const cv = this.cv;
    const src = cv.imread(canvas);
    
    try {
      // Calculate output dimensions
      const widthTop = Math.sqrt(
        Math.pow(corners[1].x - corners[0].x, 2) + 
        Math.pow(corners[1].y - corners[0].y, 2)
      );
      const widthBottom = Math.sqrt(
        Math.pow(corners[2].x - corners[3].x, 2) + 
        Math.pow(corners[2].y - corners[3].y, 2)
      );
      const maxWidth = Math.round(Math.max(widthTop, widthBottom));
      
      const heightLeft = Math.sqrt(
        Math.pow(corners[3].x - corners[0].x, 2) + 
        Math.pow(corners[3].y - corners[0].y, 2)
      );
      const heightRight = Math.sqrt(
        Math.pow(corners[2].x - corners[1].x, 2) + 
        Math.pow(corners[2].y - corners[1].y, 2)
      );
      const maxHeight = Math.round(Math.max(heightLeft, heightRight));
      
      // Source points (detected corners)
      const srcPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
        corners[0].x, corners[0].y,
        corners[1].x, corners[1].y,
        corners[2].x, corners[2].y,
        corners[3].x, corners[3].y,
      ]);
      
      // Destination points (straightened rectangle)
      const dstPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
        0, 0,
        maxWidth - 1, 0,
        maxWidth - 1, maxHeight - 1,
        0, maxHeight - 1,
      ]);
      
      // Get perspective transform matrix
      const M = cv.getPerspectiveTransform(srcPoints, dstPoints);
      
      // Apply transform
      const dst = new cv.Mat();
      const dsize = new cv.Size(maxWidth, maxHeight);
      cv.warpPerspective(src, dst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
      
      // Create output canvas
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = maxWidth;
      outputCanvas.height = maxHeight;
      cv.imshow(outputCanvas, dst);
      
      // Clean up
      srcPoints.delete();
      dstPoints.delete();
      M.delete();
      dst.delete();
      src.delete();
      
      return outputCanvas.toDataURL('image/jpeg', 0.92);
      
    } catch (err) {
      console.error('[OpenCV] Perspective transform error:', err);
      src.delete();
      return null;
    }
  }
}

/**
 * Fallback quality analysis without OpenCV
 */
const analyzeWithoutOpenCV = (canvas) => {
  const ctx = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  const warnings = [];
  let brightnessSum = 0;
  const pixelCount = data.length / 4;
  const grayscale = new Float32Array(pixelCount);
  
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
    grayscale[i / 4] = gray;
    brightnessSum += gray;
  }
  
  const brightness = brightnessSum / pixelCount;
  
  let contrastSum = 0;
  for (let i = 0; i < grayscale.length; i++) {
    contrastSum += Math.pow(grayscale[i] - brightness, 2);
  }
  const contrast = Math.sqrt(contrastSum / grayscale.length);
  
  // Simplified Laplacian variance
  let laplacianSum = 0;
  let laplacianCount = 0;
  const step = 4;
  
  for (let y = step; y < height - step; y += step) {
    for (let x = step; x < width - step; x += step) {
      const idx = y * width + x;
      const center = grayscale[idx] * 4;
      const top = grayscale[idx - width] || grayscale[idx];
      const bottom = grayscale[idx + width] || grayscale[idx];
      const left = grayscale[idx - 1] || grayscale[idx];
      const right = grayscale[idx + 1] || grayscale[idx];
      const laplacian = Math.abs(center - top - bottom - left - right);
      laplacianSum += laplacian * laplacian;
      laplacianCount++;
    }
  }
  
  const blurScore = laplacianCount > 0 ? Math.sqrt(laplacianSum / laplacianCount) : 0;
  
  // Fallback: can't detect document without OpenCV
  warnings.push({
    type: 'no_document',
    priority: 100,
    message: 'Make sure a full document page is visible and readable.',
    short: 'Verify document is visible',
  });
  
  if (brightness < 50) {
    warnings.push({ type: 'dark', ...WARNING_MESSAGES.dark });
  } else if (brightness > 210) {
    warnings.push({ type: 'bright', ...WARNING_MESSAGES.bright });
  }
  
  if (contrast < 30) {
    warnings.push({ type: 'contrast', ...WARNING_MESSAGES.contrast });
  }
  
  if (blurScore < 10) {
    warnings.push({ type: 'blur', ...WARNING_MESSAGES.blur });
  }
  
  warnings.sort((a, b) => b.priority - a.priority);
  
  return {
    brightness,
    contrast,
    blurScore,
    documentDetected: false,
    documentContour: null,
    perspectiveResult: null,
    dimensions: { width, height },
    warnings,
    status: 'needs_review',
    fallbackMode: true,
  };
};

/**
 * Mobile Document Scanner Page
 * Uses OpenCV.js for document detection and quality analysis
 */
const MobileScanPage = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionToken = searchParams.get('token');

  // State
  const [sessionStatus, setSessionStatus] = useState('loading');
  const [capturedImage, setCapturedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [imageQuality, setImageQuality] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [showGalleryOption, setShowGalleryOption] = useState(false);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [flashSupported, setFlashSupported] = useState(false);
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [cvReady, setCvReady] = useState(false);
  const [useCropped, setUseCropped] = useState(true);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const trackRef = useRef(null);
  const scannerRef = useRef(null);

  // Load OpenCV.js on mount
  useEffect(() => {
    loadOpenCV()
      .then((cv) => {
        scannerRef.current = new DocumentScanner(cv);
        setCvReady(true);
        console.log('[Scanner] OpenCV.js ready');
      })
      .catch((err) => {
        console.warn('[Scanner] OpenCV.js not available, using fallback:', err);
        setCvReady(false);
      });
  }, []);

  // Validate session on mount
  useEffect(() => {
    const validateSession = async () => {
      if (!sessionId || !sessionToken) {
        setError('Invalid scan link. Please generate a new QR code.');
        setSessionStatus('invalid');
        return;
      }

      try {
        const response = await axios.get(
          `${API}/documents/scan-sessions/${sessionId}/status`,
          { params: { token: sessionToken } }
        );

        if (response.data.status === 'expired') {
          setError('This scan session has expired. Please generate a new QR code.');
          setSessionStatus('expired');
        } else if (response.data.status === 'completed') {
          setError('This scan session has already been used.');
          setSessionStatus('completed');
        } else {
          setSessionStatus('ready');
          startCamera();
        }
      } catch (err) {
        console.error('Session validation error:', err);
        setError('Failed to validate scan session. Please try again.');
        setSessionStatus('error');
      }
    };

    validateSession();

    return () => {
      stopCamera();
    };
  }, [sessionId, sessionToken]);

  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const videoTrack = stream.getVideoTracks()[0];
      trackRef.current = videoTrack;
      
      if (videoTrack) {
        const capabilities = videoTrack.getCapabilities?.();
        if (capabilities?.torch) {
          setFlashSupported(true);
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Could not access camera. Please allow camera permissions or use gallery upload.');
      setShowGalleryOption(true);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    trackRef.current = null;
    setCameraReady(false);
  };

  const toggleFlash = async () => {
    if (!trackRef.current || !flashSupported) return;
    
    try {
      await trackRef.current.applyConstraints({
        advanced: [{ torch: !flashEnabled }]
      });
      setFlashEnabled(!flashEnabled);
    } catch (err) {
      console.error('Flash toggle error:', err);
      toast.error('Could not toggle flash');
    }
  };

  const flipCamera = () => {
    stopCamera();
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    setFlashEnabled(false);
    setTimeout(startCamera, 100);
  };

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Analyze with OpenCV if available
    let quality;
    if (cvReady && scannerRef.current) {
      quality = scannerRef.current.analyze(canvas);
      
      // Apply perspective transform if document detected
      if (quality.documentDetected && quality.perspectiveResult?.canTransform) {
        const cropped = scannerRef.current.applyPerspectiveTransform(
          canvas, 
          quality.perspectiveResult.corners
        );
        if (cropped) {
          setCroppedImage(cropped);
        }
      }
    } else {
      quality = analyzeWithoutOpenCV(canvas);
    }
    
    setImageQuality(quality);
    setCapturedImage(canvas.toDataURL('image/jpeg', 0.92));
    setShowReviewScreen(true);
    stopCamera();
  }, [cvReady]);

  const retakePhoto = () => {
    setCapturedImage(null);
    setCroppedImage(null);
    setImageQuality(null);
    setShowReviewScreen(false);
    setUseCropped(true);
    startCamera();
  };

  const handleGalleryUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        let quality;
        if (cvReady && scannerRef.current) {
          quality = scannerRef.current.analyze(canvas);
          
          if (quality.documentDetected && quality.perspectiveResult?.canTransform) {
            const cropped = scannerRef.current.applyPerspectiveTransform(
              canvas, 
              quality.perspectiveResult.corners
            );
            if (cropped) {
              setCroppedImage(cropped);
            }
          }
        } else {
          quality = analyzeWithoutOpenCV(canvas);
        }
        
        setImageQuality(quality);
        setCapturedImage(e.target.result);
        setShowReviewScreen(true);
        stopCamera();
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const proceedWithUpload = async () => {
    // Use cropped image if available and selected
    const imageToUpload = (useCropped && croppedImage) ? croppedImage : capturedImage;
    if (!imageToUpload) return;

    setUploading(true);

    try {
      const response = await fetch(imageToUpload);
      const blob = await response.blob();
      const file = new File([blob], 'scan.jpg', { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_token', sessionToken);
      formData.append('quality_status', imageQuality?.status || 'unknown');
      formData.append('quality_warnings', JSON.stringify(imageQuality?.warnings || []));
      formData.append('document_detected', imageQuality?.documentDetected || false);
      formData.append('was_cropped', useCropped && !!croppedImage);

      const uploadResponse = await axios.post(
        `${API}/documents/scan-sessions/${sessionId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (uploadResponse.data.success) {
        toast.success('Document uploaded successfully!');
        setSessionStatus('success');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Button styles for iOS Safari
  const retakeButtonStyle = {
    background: colors.darkButton,
    border: '1px solid rgba(255,255,255,0.18)',
    color: '#f9fafb',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  };

  // Loading state
  if (sessionStatus === 'loading') {
    return (
      <div 
        className="flex items-center justify-center" 
        style={{ 
          background: colors.surface,
          minHeight: '100dvh',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="text-center">
          <Loader2 size={48} className="mx-auto animate-spin" style={{ color: colors.brandGreen }} />
          <p className="mt-4 font-medium" style={{ color: colors.textPrimary }}>
            {cvReady ? 'Validating scan session...' : 'Loading scanner...'}
          </p>
        </div>
      </div>
    );
  }

  // Error states
  if (sessionStatus === 'invalid' || sessionStatus === 'expired' || sessionStatus === 'error') {
    return (
      <div 
        className="flex items-center justify-center p-6" 
        style={{ 
          background: colors.surface,
          minHeight: '100dvh',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="text-center max-w-sm">
          <div
            className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
            style={{ background: `${colors.error}15` }}
          >
            <AlertCircle size={32} style={{ color: colors.error }} />
          </div>
          <h1 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>
            {sessionStatus === 'expired' ? 'Session Expired' : 'Invalid Session'}
          </h1>
          <p className="text-sm mb-6" style={{ color: colors.textSecondary }}>
            {error}
          </p>
          <div
            className="flex items-center justify-center gap-2 p-3 rounded-lg"
            style={{ background: `${colors.brandGreen}10` }}
          >
            <Info size={18} style={{ color: colors.brandGreen }} />
            <span className="text-sm" style={{ color: colors.brandGreen }}>
              Return to desktop and generate a new QR code
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (sessionStatus === 'success') {
    return (
      <div 
        className="flex items-center justify-center p-6" 
        style={{ 
          background: colors.surface,
          minHeight: '100dvh',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="text-center max-w-sm">
          <div
            className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4"
            style={{ background: `${colors.brandGreen}15` }}
          >
            <Check size={40} style={{ color: colors.brandGreen }} />
          </div>
          <h1 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>
            Upload Complete!
          </h1>
          <p className="text-sm mb-4" style={{ color: colors.textSecondary }}>
            Your document has been uploaded successfully.
          </p>
          <div
            className="flex items-center justify-center gap-2 p-3 rounded-lg"
            style={{ background: `${colors.brandGreen}10` }}
          >
            <Smartphone size={18} style={{ color: colors.brandGreen }} />
            <span className="text-sm font-medium" style={{ color: colors.brandGreen }}>
              Safe to close this tab
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Review screen
  if (showReviewScreen && capturedImage) {
    const qualityInfo = QUALITY_STATUS[imageQuality?.status] || QUALITY_STATUS.needs_review;
    const QualityIcon = qualityInfo.icon;
    const isPoor = imageQuality?.status === 'poor';
    const isGood = imageQuality?.status === 'good';
    const hasCropped = !!croppedImage;
    const displayImage = (useCropped && croppedImage) ? croppedImage : capturedImage;
    
    return (
      <div 
        className="flex flex-col"
        style={{ 
          background: colors.dark,
          minHeight: '100dvh',
        }}
      >
        {/* Header */}
        <div 
          className="p-4 flex items-center justify-between"
          style={{ 
            background: 'rgba(0,0,0,0.9)',
            paddingTop: 'max(16px, env(safe-area-inset-top))',
          }}
        >
          <div>
            <h1 className="text-white font-semibold">Review Scan</h1>
            <p className="text-white/70 text-xs">Check quality before uploading</p>
          </div>
          <button
            onClick={retakePhoto}
            className="p-2 rounded-full"
            style={{ 
              background: 'rgba(255,255,255,0.2)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Image Preview */}
        <div className="flex-1 relative flex flex-col items-center justify-center p-4">
          <img
            src={displayImage}
            alt="Captured document"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            style={{ maxHeight: hasCropped ? '38vh' : '42vh' }}
          />
          
          {/* Document detection overlay */}
          {!imageQuality?.documentDetected && (
            <div className="absolute inset-4 border-2 border-dashed border-red-500/50 rounded-lg flex items-center justify-center pointer-events-none">
              <div className="bg-black/70 px-4 py-2 rounded-lg">
                <span className="text-red-400 text-sm font-medium flex items-center gap-2">
                  <ScanLine size={16} />
                  No document detected
                </span>
              </div>
            </div>
          )}
          
          {/* Cropped/Original toggle */}
          {hasCropped && (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => setUseCropped(true)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                  useCropped ? 'bg-emerald-600 text-white' : 'bg-white/10 text-white/70'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Crop size={14} />
                Cropped
              </button>
              <button
                onClick={() => setUseCropped(false)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                  !useCropped ? 'bg-emerald-600 text-white' : 'bg-white/10 text-white/70'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ImageIcon size={14} />
                Original
              </button>
            </div>
          )}
        </div>

        {/* Quality Panel */}
        <div 
          className="p-4"
          style={{ 
            background: 'linear-gradient(to top, rgba(0,0,0,0.98), rgba(0,0,0,0.9))',
            paddingBottom: 'max(28px, calc(env(safe-area-inset-bottom) + 20px))',
          }}
        >
          {/* Quality Badge */}
          <div 
            className="flex items-center justify-center gap-2 p-3 rounded-xl mb-4"
            style={{ background: `${qualityInfo.color}25` }}
          >
            <QualityIcon size={20} style={{ color: qualityInfo.color }} />
            <span className="font-semibold" style={{ color: qualityInfo.color }}>
              {qualityInfo.label}
            </span>
          </div>

          {/* Warnings */}
          {imageQuality?.warnings?.length > 0 && (
            <div className="mb-4 space-y-2">
              {imageQuality.warnings.slice(0, 3).map((warning, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-xl"
                  style={{ 
                    background: idx === 0 ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.08)',
                    border: idx === 0 ? '1px solid rgba(239,68,68,0.3)' : 'none',
                  }}
                >
                  <AlertTriangle 
                    size={18} 
                    className="mt-0.5 flex-shrink-0" 
                    style={{ color: idx === 0 ? '#f87171' : '#fbbf24' }}
                  />
                  <span 
                    className="text-sm leading-relaxed"
                    style={{ color: idx === 0 ? '#fecaca' : 'rgba(255,255,255,0.8)' }}
                  >
                    {warning.message}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Retake recommendation */}
          {isPoor && (
            <div 
              className="mb-4 p-3 rounded-xl flex items-center gap-3"
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}
            >
              <RotateCcw size={18} style={{ color: colors.brandGreen }} />
              <span className="text-sm" style={{ color: '#6ee7b7' }}>
                <strong>Recommended:</strong> Retake for better results
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={retakePhoto}
              disabled={uploading}
              className={`flex-1 h-14 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                isPoor ? 'order-first' : ''
              }`}
              style={{
                ...retakeButtonStyle,
                opacity: uploading ? 0.6 : 1,
              }}
              onTouchStart={(e) => { e.currentTarget.style.background = colors.darkButtonActive; }}
              onTouchEnd={(e) => { e.currentTarget.style.background = colors.darkButton; }}
              onMouseDown={(e) => { e.currentTarget.style.background = colors.darkButtonActive; }}
              onMouseUp={(e) => { e.currentTarget.style.background = colors.darkButton; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = colors.darkButton; }}
              data-testid="retake-button"
            >
              <RotateCcw size={18} style={{ color: '#f9fafb' }} />
              <span style={{ color: '#f9fafb' }}>Retake</span>
            </button>
            
            {isGood ? (
              <button
                onClick={proceedWithUpload}
                disabled={uploading}
                className="flex-1 h-14 rounded-xl font-semibold flex items-center justify-center gap-2"
                style={{ 
                  background: colors.brandGreen,
                  color: 'white',
                  opacity: uploading ? 0.6 : 1,
                  WebkitTapHighlightColor: 'transparent',
                }}
                data-testid="upload-button"
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    <span>Upload</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={proceedWithUpload}
                disabled={uploading}
                className={`flex-1 h-14 rounded-xl font-semibold flex items-center justify-center gap-2 ${
                  isPoor ? '' : 'order-first'
                }`}
                style={{ 
                  background: colors.warning,
                  color: '#1f2937',
                  opacity: uploading ? 0.6 : 1,
                  WebkitTapHighlightColor: 'transparent',
                }}
                data-testid="use-anyway-button"
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={18} />
                    <span>Use Anyway</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main scanner UI
  return (
    <div 
      className="flex flex-col"
      style={{ 
        background: colors.dark,
        minHeight: '100dvh',
      }}
    >
      {/* Header */}
      <div
        className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between"
        style={{ 
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.85), transparent)',
          paddingTop: 'max(16px, env(safe-area-inset-top))',
        }}
      >
        <div>
          <h1 className="text-white font-semibold">Scan Document</h1>
          <p className="text-white/70 text-xs">FundElite Document Scanner</p>
        </div>
        <div className="flex items-center gap-2">
          {flashSupported && (
            <button
              onClick={toggleFlash}
              className="p-2 rounded-full"
              style={{ 
                background: flashEnabled ? colors.brandGreen : 'rgba(255,255,255,0.2)',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {flashEnabled ? <Zap size={20} className="text-white" /> : <ZapOff size={20} className="text-white/70" />}
            </button>
          )}
          {cameraReady && (
            <button
              onClick={flipCamera}
              className="p-2 rounded-full"
              style={{ background: 'rgba(255,255,255,0.2)', WebkitTapHighlightColor: 'transparent' }}
            >
              <RotateCcw size={20} className="text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Camera */}
      <div className="flex-1 relative flex items-center justify-center bg-black">
        {!showGalleryOption && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Document Frame */}
        <div 
          className="absolute pointer-events-none"
          style={{ top: '15%', left: '8%', right: '8%', bottom: '28%' }}
        >
          <div className="absolute -top-10 left-0 right-0 text-center">
            <span className="text-white text-sm px-4 py-2 rounded-full bg-black/60 font-medium">
              <FileText size={14} className="inline-block mr-2 -mt-0.5" />
              Place full document inside frame
            </span>
          </div>

          <div className="absolute top-0 left-0 w-14 h-14 border-l-4 border-t-4 rounded-tl-2xl" style={{ borderColor: colors.brandGreen }} />
          <div className="absolute top-0 right-0 w-14 h-14 border-r-4 border-t-4 rounded-tr-2xl" style={{ borderColor: colors.brandGreen }} />
          <div className="absolute bottom-0 left-0 w-14 h-14 border-l-4 border-b-4 rounded-bl-2xl" style={{ borderColor: colors.brandGreen }} />
          <div className="absolute bottom-0 right-0 w-14 h-14 border-r-4 border-b-4 rounded-br-2xl" style={{ borderColor: colors.brandGreen }} />

          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <Focus size={36} className="text-white/25" />
          </div>
        </div>

        {showGalleryOption && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
            <div className="text-center p-6">
              <AlertCircle size={48} className="mx-auto mb-4 text-white/60" />
              <p className="text-white mb-4">Camera not available</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-6 py-3 rounded-xl text-white font-medium"
                style={{ background: colors.brandGreen, WebkitTapHighlightColor: 'transparent' }}
              >
                <ImageIcon size={18} className="inline-block mr-2" />
                Choose from Gallery
              </button>
            </div>
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleGalleryUpload} />

      {/* Bottom Controls */}
      <div
        className="relative z-10"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.98), rgba(0,0,0,0.85))',
          paddingTop: '20px',
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingBottom: 'max(36px, calc(env(safe-area-inset-bottom) + 24px))',
        }}
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <Focus size={18} style={{ color: colors.brandGreen }} />
          <span className="font-medium text-white">
            {cameraReady ? 'Ready to Capture' : 'Starting Camera...'}
          </span>
        </div>
        
        <p className="text-center text-white/60 text-sm mb-4">
          {cameraReady ? 'Ensure the entire document is visible and in focus' : 'Please wait...'}
        </p>

        <div className="flex items-center justify-center gap-5 mb-5 text-xs text-white/50">
          <span className="flex items-center gap-1.5"><Sun size={14} />Good lighting</span>
          <span className="flex items-center gap-1.5"><Eye size={14} />Full page</span>
          <span className="flex items-center gap-1.5"><Focus size={14} />Hold steady</span>
        </div>

        <div className="flex items-center justify-center gap-6">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-4 rounded-full"
            style={{ background: 'rgba(255,255,255,0.12)', WebkitTapHighlightColor: 'transparent' }}
          >
            <ImageIcon size={24} className="text-white" />
          </button>

          <button
            onClick={capturePhoto}
            disabled={!cameraReady}
            className="w-20 h-20 rounded-full flex items-center justify-center disabled:opacity-50 transition-transform active:scale-95"
            style={{
              background: colors.white,
              boxShadow: '0 0 0 6px rgba(255,255,255,0.3), 0 4px 24px rgba(0,0,0,0.4)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: colors.brandGreen }}>
              <Camera size={28} className="text-white" />
            </div>
          </button>

          <div className="w-14" />
        </div>
      </div>
    </div>
  );
};

export default MobileScanPage;
